import { Download } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { EditModal } from '@nucleus-commerce/admin-kit/forms';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { formatBytes, MediaThumb, useUpdateMedia } from '@/features/media';
import { MAX_MEDIA_ALT_LENGTH } from '@/shared/media/limits';

import { productKeys } from '../hooks/product-keys';
import { useProduct } from '../hooks/use-product';
import { useVariants } from '../hooks/use-variants';

import {
  isGroupOn,
  isPartialScope,
  toggleVariantGroup,
  variantGroups,
  type GalleryItem,
} from './product-media-gallery';
import { ProductMediaVariantsDialog } from './product-media-variants-dialog';

/** Search engines read about this much; longer text still saves, up to the backend's limit. */
const ALT_TARGET_LENGTH = 125;

export interface ProductMediaDetailsModalProps {
  productId: string;
  item: GalleryItem;
  /** The file is first in the gallery. */
  featured: boolean;
  /** May change this product's gallery: which variants, featured, remove. */
  canEdit: boolean;
  /** May change the file itself: its alt text. */
  canEditAlt: boolean;
  onClose: () => void;
  /** Done: the gallery changes, kept in the page's draft for the save bar. */
  onApply: (variantIds: string[], makeFeatured: boolean) => void;
  onRemove: () => void;
}

function VariantChip({
  on,
  disabled,
  onClick,
  children,
}: {
  on: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'focus-visible:ring-ring/50 rounded-full border px-2.5 py-0.5 text-xs font-semibold outline-none focus-visible:ring-[3px] disabled:cursor-default',
        on
          ? 'bg-primary-tint text-primary border-transparent'
          : 'bg-background text-muted-foreground enabled:hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

/**
 * One gallery file's details. Alt text belongs to the file in the media library, so Done writes it
 * at once; which variants show the file and whether it is featured belong to this product's
 * gallery, so Done keeps those in the page's draft and the save bar saves them.
 */
export function ProductMediaDetailsModal({
  productId,
  item,
  featured,
  canEdit,
  canEditAlt,
  onClose,
  onApply,
  onRemove,
}: ProductMediaDetailsModalProps) {
  const { t } = useTranslation(['products', 'common']);
  const { media } = item;
  const altId = useId();
  const variantsLabelId = useId();
  const featuredId = useId();
  const update = useUpdateMedia({ invalidates: [productKeys.media(productId)] });
  const product = useProduct(productId).data;
  const variants = useVariants(productId).data ?? [];

  const [alt, setAlt] = useState(media.alt ?? '');
  const [scope, setScope] = useState<string[]>(item.variantIds);
  const [makeFeatured, setMakeFeatured] = useState(false);
  const [pickingExact, setPickingExact] = useState(false);

  const option = [...(product?.options ?? [])].sort((a, b) => a.position - b.position)[0];
  const groups = variantGroups(option, variants);
  const partial = isPartialScope(scope, groups);

  const facts = [
    media.filename ?? t('products:media.untitled'),
    media.width && media.height ? `${media.width} × ${media.height}` : null,
    formatBytes(media.bytes),
  ].filter(Boolean);

  const finish = () => {
    if (canEdit) onApply(scope, makeFeatured);
    onClose();
  };
  const done = () => {
    const next = alt.trim();
    if (canEditAlt && next !== (media.alt ?? '')) {
      update.mutate({ id: media.id, body: { alt: next || null } }, { onSuccess: finish });
    } else finish();
  };

  return (
    <EditModal
      open
      onOpenChange={(open) => !open && onClose()}
      size="xl"
      title={
        media.kind === 'image'
          ? t('products:mediaDetails.title')
          : t('products:mediaDetails.fileTitle')
      }
      description={facts.join(' · ')}
      onDone={done}
      doneDisabled={update.isPending}
      footerStart={
        canEdit ? (
          <Button
            type="button"
            variant="link"
            className="text-destructive h-auto p-0"
            onClick={onRemove}
          >
            {t('products:media.remove')}
          </Button>
        ) : null
      }
      footer={
        canEdit || canEditAlt ? undefined : (
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
        )
      }
    >
      <div className="grid items-start gap-6 sm:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <div className="flex flex-col gap-2.5">
          <MediaThumb media={media} rendition="large" className="w-full rounded-xl" />
          <Button variant="outline" asChild>
            <a href={media.url} download={media.filename ?? true} target="_blank" rel="noreferrer">
              <Download aria-hidden />
              {t('products:mediaDetails.download')}
            </a>
          </Button>
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={altId}>{t('products:mediaDetails.alt')}</Label>
            <Textarea
              id={altId}
              rows={3}
              value={alt}
              maxLength={MAX_MEDIA_ALT_LENGTH}
              disabled={!canEditAlt}
              onChange={(e) => setAlt(e.target.value)}
            />
            <p className="text-muted-foreground flex justify-between gap-2 text-xs">
              <span>{t('products:mediaDetails.altHint')}</span>
              <span
                className={cn(
                  'whitespace-nowrap tabular-nums',
                  alt.length > ALT_TARGET_LENGTH && 'text-destructive',
                )}
              >
                {t('products:mediaDetails.altCounter', {
                  length: alt.length,
                  max: ALT_TARGET_LENGTH,
                })}
              </span>
            </p>
          </div>

          <div className="flex flex-col gap-2.5">
            <div>
              <h3 id={variantsLabelId} className="text-sm font-semibold">
                {t('products:mediaDetails.variants')}
              </h3>
              {option ? (
                <p className="text-muted-foreground mt-0.5 text-[13px]">
                  {t('products:mediaDetails.variantsHint', { option: option.name.toLowerCase() })}
                </p>
              ) : null}
            </div>
            <div role="group" aria-labelledby={variantsLabelId} className="flex flex-wrap gap-1.5">
              <VariantChip on={scope.length === 0} disabled={!canEdit} onClick={() => setScope([])}>
                {t('products:mediaDetails.allVariants')}
              </VariantChip>
              {groups.map((group) => (
                <VariantChip
                  key={group.value}
                  on={scope.length > 0 && isGroupOn(scope, group)}
                  disabled={!canEdit}
                  onClick={() => setScope(toggleVariantGroup(scope, group, variants.length))}
                >
                  {group.label}
                </VariantChip>
              ))}
            </div>
            {partial ? (
              <p className="text-muted-foreground text-xs">
                {t('products:mediaDetails.partialScope', { count: scope.length })}
              </p>
            ) : null}
            {canEdit && variants.length > 1 ? (
              <Button
                type="button"
                variant="link"
                size="xs"
                className="text-primary h-auto self-start p-0 text-[13px]"
                onClick={() => setPickingExact(true)}
              >
                {t('products:mediaDetails.pickExact')}
              </Button>
            ) : null}
          </div>

          <div className="flex items-start gap-3">
            <Switch
              id={featuredId}
              className="mt-0.5"
              checked={featured || makeFeatured}
              disabled={!canEdit || featured}
              onCheckedChange={setMakeFeatured}
            />
            <div className="flex flex-col gap-0.5">
              <Label htmlFor={featuredId}>{t('products:mediaDetails.featured')}</Label>
              <p className="text-muted-foreground text-xs">
                {t('products:mediaDetails.featuredHint')}
              </p>
            </div>
          </div>
        </div>
      </div>
      {pickingExact ? (
        <ProductMediaVariantsDialog
          fileCount={1}
          initial={scope}
          mixed={false}
          variants={variants}
          onClose={() => setPickingExact(false)}
          onSave={setScope}
        />
      ) : null}
    </EditModal>
  );
}
